import { Close, Delete, ResetTv } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControlLabel, IconButton, Modal, Stack } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useMessage } from '../../../../base/MessageProvider';
import ReactTextInput from '../../../../components/ControllerComponent/ReactTextInput';
import ReactiveDatePickerField from '../../../../components/ControllerComponent/ReactDatePicker';
import clsx from 'clsx';
import { addEducationData, deleteEducationData, updateEducationData } from '../../../actions/profile';
import { Bars } from 'react-loader-spinner';
import dayjs from 'dayjs';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: '90%',
    bgcolor: 'white',
    paddingX: 5,
    paddingY: 3,
    borderRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center' 
}

const parseDateString = (originalValue) => {
    const parsedDate = !isNaN(new Date(originalValue)) ? originalValue : new Date(dayjs(originalValue))
    return parsedDate;
}

const validateSchema = Yup.object().shape({
    courseName: Yup.string().required("Course name is required"),
    instituteName: Yup.string().required("Institution name is required"),
    startDate: Yup.date().typeError('Start Date is required')
        .transform(parseDateString).required("Start Date is required"),
    present: Yup.bool(),
    endDate: Yup.date().nullable().notRequired()
        .typeError('Invalid end date')
        .when('present', (value, schema) => {
            if (Number(value[0])) return schema
            else return Yup.date().typeError("End date is required").transform(parseDateString).required("End date is required")
        }),
    
})

const EducationEditModal = ({ open, onClose }) => {

    const dispatch = useDispatch();
    const alert = useMessage();
    const loading = useSelector((state) => state.artistProfileReducer?.loading);
    const educationData = useSelector((state) => state.artistProfileReducer?.selectedEducation);

    const {
        control, handleSubmit,
        setValue, watch,
        clearErrors, reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            courseName: '',
            instituteName: '',
            startDate: null,
            endDate: null,
            present: false
        },
        resolver: yupResolver(validateSchema)
    });

    const startDate = watch(`startDate`);
    const endDate = watch(`endDate`);
    const present = watch(`present`);

    useEffect(() => {
        clearErrors(); reset();
        if (educationData) {
            setValue('courseName', educationData?.courseName ?? '')
            setValue('instituteName', educationData?.instituteName);  
            setValue('startDate', educationData?.startDate ? new Date(educationData?.startDate) : null);
            setValue('endDate', educationData?.endDate ? new Date(educationData?.endDate) : null);
            setValue('present', educationData?.endDate ? false : true);
        }
    }, [educationData]);

    const onChangePresent = (event) => {
        setValue("present", event?.target.checked);
    }

    const successCallback = async (res) => {
        alert.showSuccess(res.message);
        onClose();
    }

    const errorCallback = async (error) => {
        alert.showError(error?.message ?? "Something went wrong!!");
    }

    const addHandler = async (data) => {
        dispatch(addEducationData({ ...data }, successCallback, errorCallback));
    }

    const updateHandler = async (data) => {
        dispatch(updateEducationData({ ...data, id: educationData?.id }, successCallback, errorCallback));
    }

    const deleteHandler = async () => {
        dispatch(deleteEducationData({ educationId: educationData?.id }, successCallback, errorCallback));
    }

    const onClickDeleteButton = () => {
        alert.showConfirmation2({
            message: 'Are you sure you want to delete?',
            button1Title: 'Cancel',
            button2Title: 'Delete',
            onPressBtn1: () => {},
            onPressBtn2: deleteHandler,
        })
    }

    const onSubmit = async (data) => {
        if (educationData) updateHandler(data);
        else addHandler(data);
    }

    return (
        <Modal disableAutoFocus sx={{ bgcolor: '#F4F4F499'}} hideBackdrop open={open}>
            <Box className="shadow-[0_0_14px_grey]" sx={boxStyle}>
                <IconButton onClick={onClose} className='absolute top-3 right-3' size='small'> 
                    <Close className='text-xl' />
                </IconButton>
                <Box className="mb-6">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-center text-[16px]">{!educationData ? 'Add' : 'Edit'} Education/Qualification</h2>
                </Box>
                <Stack className="w-full" my={1} spacing={2}>
                    <ReactTextInput
                        hookFormControl={control}
                        formLabel="Course Name*"
                        varName="courseName"
                        key={'courseName'}
                        placeholder="Eg. BA (Hons) Acting"
                        errorMessage={errors?.courseName?.message}
                    />
                    <ReactTextInput
                        hookFormControl={control}
                        formLabel="Institution Name*"
                        varName="instituteName"
                        key={'instituteName'}
                        placeholder="Eg. National School Of Drama"
                        errorMessage={errors?.instituteName?.message}
                    />
                    <Stack spacing={0.5}>
                        <Stack direction={'row'} spacing={2}>
                            <ReactiveDatePickerField
                                hookFormControl={control}
                                formLabel="Start Date*"
                                varName="startDate"
                                errorMessage={errors?.startDate?.message}
                                maxDate={endDate ? new Date(endDate) : new Date()}
                            />
                            <ReactiveDatePickerField
                                hookFormControl={control}
                                formLabel="End Date*"
                                varName="endDate"
                                errorMessage={errors?.endDate?.message}
                                minDate={startDate ? new Date(startDate) : undefined}
                                disabled={present}
                            />
                        </Stack>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={watch('present')} onChange={onChangePresent} 
                                    sx={{ '&.Mui-checked': { color: "#2F80ED" } }} size='small'
                                />
                            } 
                            componentsProps={{ typography: { className: "font-['Poppins-Regular'] font-[400] text-[11px] text-[#1F1E1E]" }}}
                            label="I am currently studying" 
                        />
                    </Stack>
                </Stack>
                <Stack className="w-full" alignItems={'center'} direction={'row'} spacing={1}>
                    <Button onClick={handleSubmit(onSubmit)} disabled={loading} className={clsx("bg-[#2F80ED] flex gap-3 justify-center text-[#FFF] w-full rounded-full my-2 font-['Poppins-Medium'] font-[500]", { "bg-opacity-70": loading })}>
                        Submit
                        <Bars
                            height="20"
                            width={'20'}
                            color="#FFFFFF"
                            ariaLabel="bars-loading"
                            visible={loading}
                        />
                    </Button>
                    {educationData && (
                        <IconButton onClick={onClickDeleteButton} size='small' className="bg-[red] w-[39px] h-[39px]">
                            <Delete className='text-white text-xl' />
                        </IconButton>
                    )}
                </Stack>
            </Box>
        </Modal>
    );
}

export default React.memo(EducationEditModal, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open
});